import {
  CardMeta,
  CardMetaGrid,
  ManagementCard,
  ManagementCardList,
  Pill,
} from 'components/ManagementCard'
import { banStatusStrings, commonStrings } from 'constants/strings'
import { getStatusLabel, getWebhookStatusLabel } from 'pages/Line/lineAccountView'
import { ELineWebhookStatus } from 'types'
import type { TBanStatusChannel } from 'types'
import { formatDateTime } from 'utils'
import { BanStatusLabel } from './BanStatusLabel'
import {
  canEditChannel,
  formatFriends,
  getCategoryLabel,
  getChannelCategory,
} from './banStatusView'

type PausedChannelsCardListProps = {
  channels: TBanStatusChannel[]
  loading: boolean
  emptyMessage?: string
  className?: string
  currentOrganizationId?: number
  onEdit?: (lineChannelId: number) => void
}

const headers = banStatusStrings.channels.headers

export function PausedChannelsCardList({
  channels,
  loading,
  emptyMessage = banStatusStrings.channels.empty,
  className,
  currentOrganizationId,
  onEdit,
}: PausedChannelsCardListProps) {
  return (
    <ManagementCardList
      loading={loading}
      isEmpty={channels.length === 0}
      loadingMessage={commonStrings.loading}
      emptyMessage={emptyMessage}
      className={className}
    >
      {channels.map((channel) => {
        const category = getChannelCategory(channel)
        const handleEdit = onEdit && canEditChannel(channel, currentOrganizationId)
          ? () => onEdit(channel.lineChannelId)
          : undefined
        return (
          <li key={channel.lineChannelId}>
            <ManagementCard
              onClick={handleEdit}
              ariaLabel={`${channel.organizationName} ${channel.basicId} ${commonStrings.actions.edit}`}
              title={channel.organizationName}
              titleSuffix={channel.basicId || undefined}
              badges={
                <Pill
                  tone={category === 'autoPaused' ? 'danger' : 'neutral'}
                  className="ml-auto"
                >
                  {getStatusLabel(channel.status)}
                </Pill>
              }
            >
              <CardMetaGrid cols={2}>
                <CardMeta
                  label={headers.category}
                  value={
                    <BanStatusLabel
                      label={getCategoryLabel(category)}
                      help={category}
                      helpButtonClassName="z-20"
                    />
                  }
                />
                <CardMeta
                  label={
                    <BanStatusLabel
                      label={headers.webhookStatus}
                      help="webhook"
                      helpButtonClassName="z-20"
                    />
                  }
                  value={
                    channel.webhookStatus === ELineWebhookStatus.VERIFIED ? (
                      getWebhookStatusLabel(channel.webhookStatus)
                    ) : (
                      <span className="text-danger">
                        {getWebhookStatusLabel(channel.webhookStatus)}
                      </span>
                    )
                  }
                />
                <CardMeta
                  label={headers.friends}
                  value={formatFriends(channel.activeFriends, channel.friendLimit)}
                />
                <CardMeta
                  label={
                    <BanStatusLabel
                      label={headers.updatedAt}
                      help="updatedAt"
                      helpButtonClassName="z-20"
                    />
                  }
                  value={formatDateTime(channel.updatedAt)}
                />
                <CardMeta
                  label={headers.memo}
                  value={channel.memo || banStatusStrings.channels.none}
                />
              </CardMetaGrid>
            </ManagementCard>
          </li>
        )
      })}
    </ManagementCardList>
  )
}
